import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authEnabled } from './config';
import { useTenant } from './TenantProvider';
import { useAgendas } from '../api/queries';
import type { Permissions } from '../types/agenda';

export function AccountMenu() {
  if (!authEnabled) return null;
  return <AccountMenuInner />;
}

function TenantSection({ tenants, activeTenant, switchTenant }: any) {
  if (tenants.length <= 1) return null;

  return (
    <div className="account-dropdown-tenants">
      <div className="account-dropdown-section-label">Workspace</div>
      <div className="account-dropdown-tenant-select">
        <select
          value={activeTenant?.id ?? ''}
          onChange={(e) => switchTenant(e.target.value)}
          className="account-dropdown-tenant-selector"
        >
          {tenants.map((tenant: any) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.displayName}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function PermissionsSection({ permissions, activeTenant }: { permissions: Permissions; activeTenant: any }) {
  return (
    <div className="account-dropdown-perms">
      <div className="account-dropdown-section-label">Permissions</div>
      {activeTenant && activeTenant.meetingTypes.map((mtConfig: any) => {
        const type = mtConfig.key;
        const canRead = permissions.readTypes.includes(type);
        const canWrite = permissions.writeTypes.includes(type);
        const level = !canRead ? 'none' : canWrite ? 'rw' : 'ro';
        const label = !canRead ? 'No access' : canWrite ? 'Read & write' : 'Read only';
        return (
          <div key={type} className="account-dropdown-perm-row">
            <span className="account-dropdown-perm-name">{mtConfig.label}</span>
            <span className={`account-dropdown-perm-badge ${level}`}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function AccountMenuInner() {
  const { user, logout } = useAuth0();
  const { data } = useAgendas();
  const permissions = data?.permissions;
  const { tenants, activeTenant, switchTenant } = useTenant();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const initial = (user?.name ?? user?.email ?? '?').charAt(0).toUpperCase();
  const displayName = user?.name ?? user?.email ?? 'Account';

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account-menu" ref={containerRef}>
      <button
        className="account-btn"
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        {user?.picture ? (
          <img src={user.picture} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span>{initial}</span>
        )}
      </button>

      {open && (
        <div className="account-dropdown" role="menu">
          <div className="account-dropdown-header">
            <div className="account-dropdown-avatar">
              {user?.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" />
              ) : (
                <span>{initial}</span>
              )}
            </div>
            <div className="account-dropdown-identity">
              <span className="account-dropdown-name">{displayName}</span>
              {user?.email && user?.name && (
                <span className="account-dropdown-email">{user.email}</span>
              )}
            </div>
          </div>

          {tenants.length > 0 && (
            <>
              <div className="account-dropdown-divider" />
              <TenantSection tenants={tenants} activeTenant={activeTenant} switchTenant={switchTenant} />
            </>
          )}

          {permissions && (
            <>
              <div className="account-dropdown-divider" />
              <PermissionsSection permissions={permissions} activeTenant={activeTenant} />
            </>
          )}

          <div className="account-dropdown-divider" />

          <button
            className="account-dropdown-action"
            role="menuitem"
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
